import React from 'react';
import PropTypes from 'prop-types';

import Box from 'relements/build/esm/ui/box';
import Loader from 'relements/build/esm/decorators/loader';
import Button from 'relements/build/esm/ui/button';
import Accordion from 'relements/build/esm/ui/accordion';

// Components
import Icon from '@src/components/Icon';
import ErrorBoundary from '@src/components/ErrorBoundary';

// Actions
import { createAlert } from '@src/actions/BotActions';
import { setURLMetadata } from '@src/actions/SubstoryActions';
import { getUserPartnerList } from 'actions/UserSpecificPartnerActions';
import {
  createBot,
  resetCreateBotModalStatus,
  getDomainData,
  fetchTestConfiguration,
} from '@src/actions/DomainActions';
import {
  AlertTypes,
  DEFAULT_LANGUAGE,
  FEATURE_FLAG_DATA,
  ActionTypes,
} from '@src/constants/MogamboConstants';

import { dispatch } from '@src/dispatcher';

// Stores
import BusinessStore from '@src/stores/BusinessStore';
import ActionStore from '@src/ActionStore';
import BotStore from '@src/store';

// Utils
import connection from '@src/connection';
import { isNewPipeline } from '@src/actions/_utils';

// Styles
import './EditBot.scss';

import BotProperties from '../_BotProperties';
import {
  getBotData,
  getBotMetadata,
  getBotProperties,
  sendAnalytics,
} from '../_BotProperties/utils';
import { BOT_ENV } from '../_BotProperties/constants';

function getStateFromStores() {
  return {
    languageList: BusinessStore.getSupportedLanguages(),
    businessList: BusinessStore.getBusinessList(),
    partnerIdMap: BusinessStore.getPartnerIdMap(),
    modalStatus: ActionStore.getCreateBotModalStatus(),
  };
}

@Loader({})
@connection([BusinessStore, ActionStore], getStateFromStores)
export default class EditBot extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      partners: [],
      personalities: [],
      businesses: [],
      languages: [],
      advancedSettingsOpen: false,
      botEnv: {
        base_url_test: '',
        base_url_production: '',
        active_env: BOT_ENV.TEST.value,
      },
      updatedBotEnv: {},
      supportedLanguages: [],
      domainId: '',
      isSuperman: false,
      createdAt: null,
      hybridLanguages: {},
    };
  }

  componentDidMount() {
    this._fetchData();
  }

  componentWillUnmount() {
    resetCreateBotModalStatus();
  }

  render() {
    if (this.props.modalStatus.created) {
      return this.renderBotCreationSuccess();
    }

    const {
      partners = [],
      personalities = [],
      businesses = [],
      languages = [],
      supportedLanguages = [],
      botEnv,
      domainId,
      isSuperman,
      createdAt,
      hybridLanguages,
    } = this.state;

    return (
      <div className="edit-bot-modal-body">
        <div className="edit-bot-modal-basic-section">
          <ErrorBoundary fallback={<span>Unable to show</span>}>
            <BotProperties.Basic
              ref={(BasicProperties) => {
                this._BasicProperties = BasicProperties;
              }}
              businesses={businesses}
              partners={partners}
              languages={languages}
              supportedLanguages={supportedLanguages}
              hybridLanguages={hybridLanguages}
              domainId={domainId}
              isSuperman={isSuperman}
              createdAt={createdAt}
            />
          </ErrorBoundary>
        </div>
        <Accordion>
          <Accordion.Item
            active={this.state.advancedSettingsOpen}
            onChange={this._toggleAdvancedSettings}
            renderHeader={this.renderAdvancedOptionsHeader}
            className={`${
              this.state.advancedSettingsOpen
                ? 'edit-bot-modal-accordion-header'
                : ''
            }`}
          >
            <div className="edit-bot-modal-advanced-section-body">
              <BotProperties.Advanced
                ref={(AdvancedProperties) => {
                  this._AdvancedProperties = AdvancedProperties;
                }}
                botEnv={botEnv}
                handleEnvUpdate={this.handleEnvUpdate}
                personalities={personalities}
              />
            </div>
          </Accordion.Item>
        </Accordion>
        <Box justifyContent="flex-end" padding={16}>
          <Button
            themed
            className="edit-bot-modal-button"
            onClick={this._handleSave}
            type={Button.TYPES.PRIMARY}
            disabled={BotStore.isProductionEnv()}
            variant={BotStore.isProductionEnv() ? 'disabled' : 'primary'}
          >
            Save
          </Button>
        </Box>
      </div>
    );
  }

  handleEnvUpdate = (key, value) => {
    this.setState({
      botEnv: {
        ...this.state.botEnv,
        [key]: value,
      },
      updatedBotEnv: {
        ...this.state.updatedBotEnv,
        [key]: value,
      },
    });
  };

  renderAdvancedOptionsHeader({ ExpandIcon }) {
    return (
      <div className="edit-bot-modal-advanced-section-header">
        <Icon
          iconType="gear"
          className="edit-bot-modal-advanced-section-header-icon"
        />
        Advanced Options
        <ExpandIcon />
      </div>
    );
  }

  renderBotCreationSuccess() {
    return (
      <div className="edit-bot-modal-success">
        <div>
          <Icon iconType="tick-green" size={60} />
        </div>
        <div>Your bot has been edited successfully.</div>
      </div>
    );
  }

  _toggleAdvancedSettings = () => {
    this.setState(
      { advancedSettingsOpen: !this.state.advancedSettingsOpen },
      () => {
        this._AdvancedProperties.setState(this.state.advancedProperties);
      }
    );
  };

  // API call for bot data
  _fetchData = async () => {
    this.props.activateLoader();
    let partners = [];

    await getUserPartnerList((success, response) => {
      if (!success) {
        createAlert(response.error, null, AlertTypes.NEGATIVE);
        return;
      }
      partners = response.body.partner_list;
    });
    const data = await getDomainData(this.props.botData.id);

    if (!data) return;

    dispatch(ActionTypes.SET_CURRENT_BOT_SETTINGS, {
      settings: data,
    });

    const languageList = this.props.allLanguages;
    const botData = { ...this.props.botData, ...data };
    const { personalities = [], languages = [] } = getBotMetadata(languageList);

    const { basicProperties, advancedProperties } = getBotProperties(
      botData,
      data.bot_properties,
      { partners, languages }
    );

    this.setState({
      partners,
      personalities,
      languages,
      supportedLanguages: botData.supported_languages,
      botEnv: data.integration_config,
      advancedProperties,
      domainId: botData.domain_id,
      isSuperman: isNewPipeline(botData),
      createdAt: new Date(botData.created_at.$date),
      hybridLanguages: botData.hybrid_languages || {},
    });

    basicProperties.business_via_name = botData.test_business;

    this._BasicProperties.setState(basicProperties);
    this.props.deactivateLoader();
  };

  _handleSave = () => {
    const { testBusiness, testPartner } = this._BasicProperties.state;

    let error = false;
    let updatedBot = getBotData({
      botId: this.props.botData.id,
      basicProperties: this._BasicProperties,
      integration_config: this.state.botEnv,
      advancedProperties: this._AdvancedProperties,
      onError: (err) => {
        this.props.deactivateLoader();
        createAlert(err, null, AlertTypes.NEGATIVE);
        error = true;
      },
      editMode: true,
      fallbackAdvancedProperties: this.state.advancedProperties,
    });

    const supportedLanguageData = this._BasicProperties.state
      .languagesSupported;

    updatedBot = {
      ...updatedBot,
      supported_languages: supportedLanguageData || [DEFAULT_LANGUAGE],
    };

    if (error || !updatedBot) return;

    this.props.activateLoader();
    sendAnalytics(
      testBusiness,
      testPartner,
      this.state.updatedBotEnv,
      this.props.botData.title
    );

    createBot(
      updatedBot,
      (createBotresponse) => {
        setURLMetadata();

        // Refresh the constant drawer list
        BotStore.getConstantDrawerRef()?.current?.refreshData();

        if (createBotresponse.success) {
          getDomainData(this.props.botData.id).then((domainDataResponse) => {
            this.props.deactivateLoader();
            const { body, meta } = createBotresponse;
            dispatch(ActionTypes.CREATE_BOT_SUCCESS, {
              data: body,
              meta,
            });
            if (domainDataResponse) {
              dispatch(ActionTypes.SET_CURRENT_BOT_SETTINGS, {
                settings: domainDataResponse,
              });
            }
          });
          fetchTestConfiguration(this.props.botData.id);
        } else this.props.deactivateLoader();
      },
      false
    );
  };
}

EditBot.propTypes = {
  businessList: PropTypes.arrayOf(PropTypes.object),
  partnerIdMap: PropTypes.object,
  modalStatus: PropTypes.object,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
};
